// src/components/ConnectViaLink.js
import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import memberstackDOM from "@memberstack/dom";
import { getMemberstackKey } from "../utils";
import { standardUserApi } from "../services/standardUserApi";
import useNoIndex from "../hooks/useNoIndex";
import Avatar from "./Avatar";
import LoadingState from "./LoadingState";
import { colors, type, radii, shadow, buttonPrimary, buttonSecondary, primaryHover } from "../styles/designTokens";

const currentKey = getMemberstackKey();
const memberstack = memberstackDOM.init({ publicKey: currentKey });

export const PENDING_CONNECT_TOKEN_KEY = "pendingConnectToken";

/**
 * Public landing page for a subscriber's personal "connect with me" link
 * (see ConnectionInviteLink in models.py). Unlike the self-run-event guest
 * RSVP or public wishlist pages, there's no guest concept here - a
 * Connection needs two real user rows, so a visitor with no OkGather
 * account has to sign up (and subscribe) before the request can send.
 * That signup step is the point: sharing this link is itself a growth
 * channel, not just a convenience for existing subscribers. Copy and
 * layout are deliberately closer to a personal invitation than a form -
 * this is the page that turns a friend into a subscriber.
 */
const ConnectViaLink = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  useNoIndex();

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [checkingMember, setCheckingMember] = useState(true);
  const [isSubscriber, setIsSubscriber] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const memberToken = localStorage.getItem("memberstackToken");

  useEffect(() => {
    const load = async () => {
      try {
        const data = await standardUserApi.getConnectionLinkPreview(token);
        setPreview(data);
      } catch (err) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  useEffect(() => {
    const checkMembership = async () => {
      if (!memberToken) {
        setCheckingMember(false);
        return;
      }
      try {
        const currentMember = await memberstack.getCurrentMember();
        const hasValidStatus = (currentMember?.data?.planConnections || []).some((plan) => {
          const status = plan.status?.toLowerCase();
          const type = plan.type?.toLowerCase();
          return type === "subscription" && ["active", "trialing", "cancelled", "canceled"].includes(status);
        });
        setIsSubscriber(hasValidStatus);
      } catch (err) {
        console.error("ConnectViaLink › getCurrentMember error:", err);
      } finally {
        setCheckingMember(false);
      }
    };
    checkMembership();
  }, [memberToken]);

  const handleSignupOrLogin = (path) => {
    localStorage.setItem(PENDING_CONNECT_TOKEN_KEY, token);
    navigate(path);
  };

  const handleConnect = async () => {
    setSending(true);
    setErrorMessage("");
    try {
      await standardUserApi.acceptConnectionViaLink(memberToken, token);
      setSent(true);
    } catch (err) {
      console.error("ConnectViaLink › accept error:", err);
      setErrorMessage(err.message || "Couldn't send that connection request right now. Please try again shortly.");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <LoadingState />;
  }

  if (notFound || !preview) {
    return (
      <div style={{ textAlign: "center", padding: "100px 20px" }}>
        <p style={{ fontSize: "2rem", marginBottom: "12px" }} aria-hidden="true">🔗</p>
        <h4 style={{ color: colors.ink, fontSize: "1.4rem", fontWeight: "800", marginBottom: "8px" }}>This invite link isn't valid</h4>
        <p style={{ color: colors.muted, fontSize: "0.95rem" }}>It may be incorrect, or no longer active.</p>
      </div>
    );
  }

  const { ownerFirstName } = preview;

  return (
    <div style={{ minHeight: "100vh", background: colors.gradientTint }}>
      <div style={{ maxWidth: "480px", margin: "0 auto", padding: "64px 20px" }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: "22px" }}>
          <Avatar firstName={ownerFirstName} lastName="" size={72} />
        </div>
        <p style={{ ...type.eyebrow, textAlign: "center" }}>You're invited to connect</p>
        <h1 style={{ ...type.h1, textAlign: "center", marginTop: "10px", marginBottom: "12px" }}>
          {ownerFirstName} wants you in their circle on OkGather
        </h1>
        <p style={{ ...type.body, textAlign: "center", maxWidth: "380px", margin: "0 auto 32px" }}>
          Connections plan hangouts together in seconds, swap wishlist ideas, and get a nudge before
          they lose touch. Say yes, and you're in {ownerFirstName}'s circle.
        </p>

        <div style={{
          background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radii.lg,
          padding: "28px 26px", textAlign: "center", boxShadow: shadow.card,
        }}>
          {sent ? (
            <>
              <p style={{ fontSize: "2rem", marginBottom: "8px" }} aria-hidden="true">🎉</p>
              <p style={{ color: colors.success, fontWeight: "800", fontSize: "1.1rem", marginBottom: "10px" }}>
                Request sent!
              </p>
              <p style={{ color: colors.muted, fontSize: "0.9rem", marginBottom: "22px", lineHeight: 1.5 }}>
                {ownerFirstName} will see it next time they check their connections.
              </p>
              <Link to="/connections" style={buttonPrimary} {...primaryHover}>
                Go to My Connections
              </Link>
            </>
          ) : checkingMember ? (
            <p style={{ color: colors.muted, fontSize: "0.9rem" }}>Checking your account...</p>
          ) : memberToken && isSubscriber ? (
            <>
              {errorMessage && <p style={{ color: colors.danger, fontSize: "0.9rem", marginBottom: "14px" }}>{errorMessage}</p>}
              <button
                onClick={handleConnect}
                disabled={sending}
                style={{ ...buttonPrimary, width: "100%", opacity: sending ? 0.7 : 1, cursor: sending ? "default" : "pointer" }}
                onMouseEnter={sending ? undefined : primaryHover.onMouseEnter}
                onMouseLeave={sending ? undefined : primaryHover.onMouseLeave}
              >
                {sending ? "Sending..." : `Connect with ${ownerFirstName}`}
              </button>
            </>
          ) : memberToken ? (
            <>
              <p style={{ color: colors.body, fontSize: "0.95rem", marginBottom: "20px", lineHeight: "1.55" }}>
                You'll need an active OkGather membership to connect with {ownerFirstName}.
              </p>
              <button
                onClick={() => handleSignupOrLogin("/checkout")}
                style={{ ...buttonPrimary, width: "100%" }}
                {...primaryHover}
              >
                Start your membership
              </button>
            </>
          ) : (
            <>
              <p style={{ color: colors.body, fontSize: "0.95rem", marginBottom: "20px", lineHeight: "1.55" }}>
                One quick, free account and you're connected with {ownerFirstName} — plus everyone
                else you meet along the way.
              </p>
              <button
                onClick={() => handleSignupOrLogin("/gathering-matcher")}
                style={{ ...buttonPrimary, width: "100%", marginBottom: "12px" }}
                {...primaryHover}
              >
                Create my free account
              </button>
              <button
                onClick={() => handleSignupOrLogin("/login")}
                style={{ ...buttonSecondary, width: "100%", boxSizing: "border-box" }}
              >
                I already have an account
              </button>
            </>
          )}
        </div>

        <p style={{ ...type.small, textAlign: "center", marginTop: "24px" }}>
          OkGather is how people turn "we should hang out sometime" into an actual plan.
        </p>
      </div>
    </div>
  );
};

export default ConnectViaLink;
